import { Metric, WorkoutLayerDomain } from '@strength/domain';
import {
  HasWorkoutLayer,
  WorkoutExerciseMetric,
  WorkoutSection,
  WorkoutSet,
  WorkoutLayerBuilder,
  SectionMutationData,
  ExerciseMutationData,
  ExerciseMetricMutationData,
  SetFullMutationData,
  SupersetExerciseMutationData,
  SupersetMutationData,
  AlternateGroupMutationData,
  SetMutationData,
  SetMetricMutationData,
  SupersetExerciseFullMutationData,
  EntryFullMutationData,
  Layer,
} from '@strength/domain-workout';
import { v4 } from 'uuid';
import { hasDisabledItem } from '../copy-paste/group-data';
import { SectionGroupedData, WorkoutGroupedData } from '../types';
import { buildResponse } from '../utils';
import {
  WorkoutLayerCommand,
  WorkoutLayerIdentity,
  WorkoutLayerModelName,
  WorkoutLayerMutationFns,
} from './types';
import {
  ensureSetsMetricInExercise,
  ensureDefaultSetWithSetsMetric,
  normalizeEntriesForDraft,
} from '../utils';
import { Result } from '@strength/utils';

/**
 * Create an edit command for the layer property of a workout. This accepts multiple changes to the layer and will convert them into a single edit command.
 * To create multiple edit commands, call this function multiple times.
 */
export function createWorkoutLayerEdit(
  identity: WorkoutLayerIdentity,
  workoutLayer: HasWorkoutLayer | undefined,
  edits: WorkoutLayerDomain.WorkoutLayerEdit[],
  workoutName?: string | null,
): WorkoutLayerCommand {
  const originalLayer =
    identity.exists && workoutLayer
      ? workoutLayer.layer
      : WorkoutLayerDomain.defaultLayer();
  // Callers may combine edits from several builders, each of which resolved its deletes
  // against the same unmodified layer
  const layer = WorkoutLayerDomain.mergeLayerEdits(
    originalLayer,
    WorkoutLayerDomain.dedupeLayerEdits(edits),
  );

  if (
    identity.modelName === 'BlockDraftWorkoutLayer' ||
    identity.modelName === 'AthleteDraftWorkoutLayer'
  ) {
    return {
      identity,
      edits: [
        {
          modelId: identity.id,
          modelName: identity.modelName,
          action: 'update',
          data: {
            layer,
            ...(workoutName === undefined ? {} : { name: workoutName }),
          },
        },
      ],
    };
  }
  return {
    identity,
    edits: [
      {
        modelId: identity.id,
        modelName: identity.modelName,
        action: 'update',
        data: {
          layer,
        },
      },
    ],
  };
}

export function makeWorkoutLayerMutationFns(
  modelName: WorkoutLayerModelName,
  workoutLayers: HasWorkoutLayer[],
  mutateFn: (command: WorkoutLayerCommand) => void,
  setsMetric: Metric,
  baseLayers?: HasWorkoutLayer[],
): WorkoutLayerMutationFns {
  function createMutateFn<FPARAM extends unknown[], MPARAM extends unknown[]>(
    modelName: WorkoutLayerModelName,
    fn: (...params: FPARAM) => WorkoutLayerDomain.WorkoutLayerEdit[],
    includeLayer = false,
    includeModelName = false,
    includeBaseLayer = false,
    workoutName?: string | null,
    setsMetric?: Metric,
  ) {
    return (workoutId: string, ...params: MPARAM) => {
      const { identity, workoutLayer, baseLayer } = findWorkoutLayer(
        workoutLayers,
        workoutId,
        modelName,
        baseLayers,
      );

      const fnParams = [
        ...(includeLayer ? [workoutLayer] : []),
        ...(includeModelName ? [modelName] : []),
        ...(includeBaseLayer ? [baseLayer] : []),
        ...(setsMetric ? [setsMetric] : []),
        ...params,
      ];
      const edits = fn(...(fnParams as FPARAM));

      if (!edits?.length) return;

      const workoutLayerEdit = createWorkoutLayerEdit(
        identity,
        workoutLayer,
        edits,
        workoutName,
      );
      mutateFn(workoutLayerEdit);
    };
  }

  return {
    // Workout operations
    onClearWorkout: createMutateFn(
      modelName,
      clearWorkoutEdits,
      true,
      false,
      true,
      null,
    ),
    // Section operations
    onAddSection: createMutateFn(modelName, addSectionEdits),
    onEditSection: createMutateFn(modelName, editSectionEdits, true),
    onRemoveSection: createMutateFn(modelName, removeSectionEdits, true),
    // Exercise operations
    onAddExercise: createMutateFn(
      modelName,
      addExerciseEdits,
      false,
      false,
      false,
      undefined,
      setsMetric,
    ),
    onEditExercise: createMutateFn(modelName, editExerciseEdits, true),
    onRemoveExercise: createMutateFn(
      modelName,
      removeExerciseEdits,
      true,
      false,
      true,
    ),
    // Superset operations
    onAddSuperset: createMutateFn(modelName, addSupersetEdits),
    onEditSuperset: createMutateFn(modelName, editSupersetEdits, true),
    onRemoveSuperset: createMutateFn(modelName, removeSupersetEdits, true),
    // Superset exercise operations
    onAddSupersetExercise: createMutateFn(modelName, addSupersetExerciseEdits),
    onEditSupersetExercise: createMutateFn(
      modelName,
      editSupersetExerciseEdits,
      true,
    ),
    onRemoveSupersetExercise: createMutateFn(
      modelName,
      removeSupersetExerciseEdits,
      true,
    ),
    // Alternate exercise operations
    onAddAlternateExercise: createMutateFn(
      modelName,
      addAlternateExerciseEdits,
    ),
    onEditAlternateExercise: createMutateFn(
      modelName,
      editAlternateExerciseEdits,
      true,
    ),
    onRemoveAlternateExercise: createMutateFn(
      modelName,
      removeAlternateExerciseEdits,
      true,
    ),
    // Alternate group operations
    onAddAlternateGroup: createMutateFn(modelName, addAlternateGroupEdits),
    onEditAlternateGroup: createMutateFn(
      modelName,
      editAlternateGroupEdits,
      true,
    ),
    onRemoveAlternateGroup: createMutateFn(
      modelName,
      removeAlternateGroupEdits,
      true,
      false,
      true,
    ),
    // Exercise metric operations
    onAddExerciseMetric: createMutateFn(
      modelName,
      addExerciseMetricEdits,
      true,
      false,
      true,
      undefined,
      setsMetric,
    ),
    onEditExerciseMetric: createMutateFn(
      modelName,
      editExerciseMetricEdits,
      true,
    ),
    onRemoveExerciseMetric: createMutateFn(
      modelName,
      removeExerciseMetricEdits,
      true,
    ),
    onRemoveExerciseMetrics: createMutateFn(
      modelName,
      removeExerciseMetricsEdits,
      true,
    ),
    onSwapExerciseMetric: createMutateFn(
      modelName,
      swapExerciseMetricEdits,
      true,
      false,
      true,
      undefined,
      setsMetric,
    ),
    // Set operations
    onAddSet: createMutateFn(
      modelName,
      addSetEdits,
      false,
      false,
      false,
      undefined,
      setsMetric,
    ),
    onEditSet: createMutateFn(modelName, editSetEdits, true),
    onRemoveSet: createMutateFn(modelName, removeSetEdits, true),
    onRemoveSets: createMutateFn(modelName, removeSetsEdits, true),
    // Set metric operations
    onEditSetMetric: createMutateFn(modelName, editSetMetricEdits, true),
    onEditSetMetrics: createMutateFn(modelName, editSetMetricsEdits, true),
    // MISC
    onSwapExercise: createMutateFn(
      modelName,
      swapExerciseEdits,
      true,
      true,
      false,
      undefined,
      setsMetric,
    ),
    onMoveEntriesIntoNewSection: createMutateFn(
      modelName,
      moveEntriesIntoNewSectionEdits,
      true,
      false,
      false,
      undefined,
      setsMetric,
    ),
  };
}

export function findWorkoutLayer(
  workoutLayers: HasWorkoutLayer[],
  workoutId: string,
  modelName: WorkoutLayerModelName,
  baseLayers?: HasWorkoutLayer[],
): {
  workoutLayer: HasWorkoutLayer | undefined;
  baseLayer: HasWorkoutLayer | undefined;
  identity: WorkoutLayerIdentity;
} {
  // SC-5512: resolution is by workout id ONLY. An empty-slot cell's key is a
  // (cycle, session) pair, never a workout id, so it never binds a live row —
  // its actions create (slot from the key) instead of mutating a row that
  // merely occupies that slot. Displayed positions can diverge from stored
  // slots (athlete-only insertions bump), so binding by slot would misfire.
  const workoutLayer = workoutLayers.find((m) => m.workoutId === workoutId);
  const baseLayer = baseLayers?.find((m) => m.workoutId === workoutId);

  if (!workoutLayer) {
    return {
      workoutLayer,
      baseLayer,
      identity: { id: v4(), workoutId, exists: false, modelName },
    };
  } else {
    return {
      workoutLayer,
      baseLayer,
      identity: {
        workoutId,
        id: workoutLayer.id,
        exists: true,
        modelName,
      },
    };
  }
}

/**
 * Functions below here convert mutations of workout entities into "edits" of the corresponding workout layer entities.
 * This is necessary because a workout layer entity may be originally from a different layer, which will change
 * the type of "edit" that needs to get produced.
 *
 * Add functions:
 *   Creates an "add" entity on the current layer.
 *   For convenience, these functions also accept "child" entity mutation data to create in the same set of edits.
 *
 * Edit functions
 *   If the entity matches an "add" on the current layer, updates the "add" entity.
 *   Otherwise, creates or updates the matching "edit" entity.
 *
 * Remove functions
 *   If the entity matches an "add" on this layer, removes the "add" entity.
 *   Otherwise creates a "removal" entity.
 *   Also removes any related entities.
 */

// ============================================================================
// Workout operations
// ============================================================================
export function clearWorkoutEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  baseLayer: HasWorkoutLayer | undefined,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder({ workoutLayer })
    .clearWorkout(baseLayer?.layer)
    .getEdits();
}

// ============================================================================
// Section operations
// ============================================================================
export function addSectionEdits(
  data: SectionMutationData,
  _workoutSectionId?: string,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const workoutSectionId = _workoutSectionId ?? v4();
  return new WorkoutLayerBuilder()
    .addSection({ workoutSectionId, data })
    .getEdits();
}

export function editSectionEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutSectionId: string,
  data: Partial<SectionMutationData>,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder({ workoutLayer })
    .editSection({ workoutSectionId, data })
    .getEdits();
}

export function removeSectionEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutSectionId: string,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder({ workoutLayer })
    .removeSection({ workoutSectionId })
    .getEdits();
}

// ============================================================================
// Exercise operations
// ============================================================================
export function addExerciseEdits(
  setsMetric: Metric,
  workoutSectionId: string,
  data: ExerciseMutationData,
  metrics: ExerciseMetricMutationData[],
  sets: SetFullMutationData[] = [],
  altGroupId?: string,
  workoutSupersetId?: string,
  _workoutExerciseId?: string,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const workoutExerciseId = _workoutExerciseId ?? v4();
  const normalizedMetrics = ensureSetsMetricInExercise(metrics, setsMetric);
  const normalizedSets = ensureDefaultSetWithSetsMetric(sets, setsMetric);

  return new WorkoutLayerBuilder()
    .addExercise({
      workoutSectionId,
      workoutExerciseId,
      workoutAlternateGroupId: altGroupId,
      workoutSupersetId,
      data,
    })
    .addExerciseMetrics({ workoutExerciseId, metrics: normalizedMetrics })
    .addSets({ workoutExerciseId, sets: normalizedSets })
    .getEdits();
}

export function editExerciseEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutExerciseId: string,
  data: Partial<ExerciseMutationData>,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder({ workoutLayer })
    .editExercise({ workoutExerciseId, data })
    .getEdits();
}

/**
 * Contains the logic to remove an exercise from the Block/Athlete draft workout layers.
 * The builder handles the cascade delete for the layer we are targeting to apply edits.
 * However, when dealing with athlete layers, we need to check the base layer to manually see if we need to cascade any deletes on the alternate group or superset.
 *
 * Good Edge Cases for testing:
 * Add an alternate group with alternate exercises on the base layer, remove base exercise on the athlete layer: group + alternate exercises should be removed on athlete layer
 * Add a superset with 2 exercises on the base layer, add an exercise to the superset on the athlete layer: delete on of the exercises should keep the superset on athlete layer
 * Add a superset with 2 exercises on the base layer, remove one of the exercises on the athlete layer: superset should be removed on athlete layer
 */
export function removeExerciseEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  baseLayer: HasWorkoutLayer | undefined,
  workoutExerciseId: string,
  workoutAlternateGroupId?: string,
  workoutSupersetId?: string,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const builder = new WorkoutLayerBuilder({
    workoutLayer,
  });
  builder.removeExercise({ workoutExerciseId });
  if (workoutSupersetId) {
    const baseSupersetExercises = Layer(baseLayer).supersetExerciseAdds({
      workoutSupersetId,
    });
    const supersetExercises = [
      ...Layer(workoutLayer).supersetExerciseAdds({ workoutSupersetId }),
      ...baseSupersetExercises,
    ];
    const targetSupersetExercise = supersetExercises.find(
      (ex) => ex.workoutExerciseId === workoutExerciseId,
    );

    if (targetSupersetExercise) {
      // will check if the supersetGroup needs to be removed in removeSupersetExercise
      builder.removeSupersetExercise({
        workoutSupersetExerciseId:
          targetSupersetExercise.workoutSupersetExerciseId,
        workoutSupersetId: targetSupersetExercise.workoutSupersetId,
        otherSupersetExercises: baseSupersetExercises,
      });
    }
  }

  // if exercise is base exercise in workoutAlternateGroupId, remove the alternate group
  if (workoutAlternateGroupId) {
    const baseAlternateExercises = Layer(baseLayer).alternateExerciseAdds({
      workoutAlternateGroupId,
    });
    const alternateExercises = [
      ...Layer(workoutLayer).alternateExerciseAdds({
        workoutAlternateGroupId,
      }),
      ...baseAlternateExercises,
    ];

    const targetAlternateExercise = alternateExercises.find(
      (exercise) => exercise.workoutExerciseId === workoutExerciseId,
    );

    if (targetAlternateExercise) {
      builder.removeAlternateExercise({
        workoutAlternateExerciseId:
          targetAlternateExercise.workoutAlternateExerciseId,
        workoutAlternateGroupId,
        otherAlternateExercises: baseAlternateExercises,
      });
    }
  }

  return builder.getEdits();
}

// ============================================================================
// Superset operations
// ============================================================================
export function addSupersetEdits(
  workoutSectionId: string,
  supersetSortOrder: number,
  supersetExercises: SupersetExerciseMutationData[],
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const builder = new WorkoutLayerBuilder();
  const workoutSupersetId = v4();

  builder.addSuperset({
    workoutSectionId,
    sortOrder: supersetSortOrder,
    supersetExercises,
    workoutSupersetId,
  });

  return builder.getEdits();
}

export function editSupersetEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutSupersetId: string,
  data: Partial<SupersetMutationData>,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder({ workoutLayer })
    .editSuperset({ workoutSupersetId, data })
    .getEdits();
}

export function removeSupersetEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutSupersetId: string,
  exerciseSortOrders?: SupersetExerciseMutationData[],
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder({ workoutLayer })
    .removeSuperset({ workoutSupersetId, exerciseSortOrders })
    .getEdits();
}

// ============================================================================
// Superset exercise operations
// ============================================================================
export function addSupersetExerciseEdits(
  workoutSupersetId: string,
  { workoutExerciseId, sortOrder }: SupersetExerciseMutationData,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder()
    .addSupersetExercise({ workoutExerciseId, workoutSupersetId, sortOrder })
    .getEdits();
}

export function editSupersetExerciseEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutSupersetExerciseId: string,
  data: Pick<SupersetExerciseMutationData, 'sortOrder'>,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder({ workoutLayer })
    .editSupersetExercise({ workoutSupersetExerciseId, data })
    .getEdits();
}

/**
 * Contains the logic to remove an exercise from a superset on the Block/Athlete draft workout layers.
 * However, when dealing with athlete layers, we need to check the base layer to manually see if we need remove the superset entirely or just the exercise..
 */
export function removeSupersetExerciseEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  baseLayer: HasWorkoutLayer | undefined,
  workoutSupersetId: string,
  workoutSupersetExerciseId: string,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const builder = new WorkoutLayerBuilder({
    workoutLayer,
  });

  const otherSupersetExercises = Layer(baseLayer).supersetExerciseAdds(
    'workoutSupersetId',
    workoutSupersetId,
  );

  // Check if the supersetGroup needs to be removed in removeSupersetExercise
  builder.removeSupersetExercise({
    workoutSupersetExerciseId,
    workoutSupersetId,
    otherSupersetExercises,
  });

  return builder.getEdits();
}

// ============================================================================
// Alternate exercise operations
// ============================================================================
export function addAlternateExerciseEdits(
  data: ExerciseMutationData,
  workoutAlternateGroupId: string,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder()
    .addAlternateExercise({
      workoutExerciseId: data.workoutExerciseId,
      workoutAlternateGroupId,
      data: { sortOrder: data.sortOrder },
    })
    .getEdits();
}

export function editAlternateExerciseEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutAlternateExerciseId: string,
  data: Pick<ExerciseMutationData, 'sortOrder'>,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder({ workoutLayer })
    .editAlternateExercise({ workoutAlternateExerciseId, data })
    .getEdits();
}

export function removeAlternateExerciseEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutAlternateExerciseId: string,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder({ workoutLayer })
    .removeAlternateExercise({ workoutAlternateExerciseId })
    .getEdits();
}

// ============================================================================
// Alternate group operations
// ============================================================================
export function addAlternateGroupEdits(
  workoutSectionId: string,
  workoutExerciseId: string,
  data: AlternateGroupMutationData,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder()
    .addAlternateGroup({
      workoutSectionId,
      workoutExerciseId,
      sortOrder: data?.sortOrder ?? 0,
    })
    .getEdits();
}

export function editAlternateGroupEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutAlternateGroupId: string,
  data: AlternateGroupMutationData,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder({ workoutLayer })
    .editAlternateGroup({ workoutAlternateGroupId, data })
    .getEdits();
}

/**
 * Contains the logic to remove an alternate group from the Block/Athlete draft workout layers.
 * However, when dealing with athlete layers, we need to check the base layer to manually see if we need remove any other alternate exercises.
 */
export function removeAlternateGroupEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  baseLayer: HasWorkoutLayer | undefined,
  workoutAlternateGroupId: string,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const builder = new WorkoutLayerBuilder({
    workoutLayer,
  });

  builder.removeAlternateGroup({ workoutAlternateGroupId });

  const maybeBaseAlternateExercises = Layer(baseLayer).alternateExerciseAdds(
    'workoutAlternateGroupId',
    workoutAlternateGroupId,
  );

  maybeBaseAlternateExercises.forEach((exercise) => {
    if (exercise.sortOrder !== 0) {
      builder.removeExercise({
        workoutExerciseId: exercise.workoutExerciseId,
      });
      builder.removeAlternateExercise({
        workoutAlternateExerciseId: exercise.workoutAlternateExerciseId,
      });
    }
  });

  return builder.getEdits();
}

// ============================================================================
// Exercise metric operations
// ============================================================================
export function addExerciseMetricEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  baseLayer: HasWorkoutLayer | undefined,
  setsMetric: Metric,
  workoutExerciseId: string,
  data: ExerciseMetricMutationData,
  preserveIds = false,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  // Get base layer metrics for this exercise to avoid duplicating them
  const baseLayerMetrics = Layer(baseLayer).exerciseMetricAdds({
    workoutExerciseId,
  });

  const normalizedMetrics = ensureSetsMetricInExercise(
    [data],
    setsMetric,
    baseLayerMetrics,
  );

  const baseLayerSets = Layer(baseLayer).setAdds({ workoutExerciseId });

  // The WorkoutLayerBuilder.addExerciseMetrics will automatically create
  // setMetrics for all existing sets when adding new metrics
  return new WorkoutLayerBuilder({ workoutLayer })
    .addExerciseMetrics({
      workoutExerciseId,
      metrics: normalizedMetrics,
      otherSets: baseLayerSets,
      preserveIds,
    })
    .getEdits();
}

export function editExerciseMetricEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutExerciseMetricId: string,
  data: Partial<ExerciseMetricMutationData>,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder({ workoutLayer })
    .editExerciseMetric({
      workoutExerciseMetricId,
      data: data,
    })
    .getEdits();
}

export function removeExerciseMetricEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutExerciseMetricId: string,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder({ workoutLayer: workoutLayer?.layer })
    .removeExerciseMetric({ workoutExerciseMetricId })
    .getEdits();
}

export function removeExerciseMetricsEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutExerciseMetricIds: string[],
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const builder = new WorkoutLayerBuilder({
    workoutLayer: workoutLayer?.layer,
  });
  for (const workoutExerciseMetricId of workoutExerciseMetricIds) {
    builder.removeExerciseMetric({ workoutExerciseMetricId });
  }
  return builder.getEdits();
}

export function swapExerciseMetricsEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  baseLayer: HasWorkoutLayer | undefined,
  setsMetric: Metric,
  workoutExerciseId: string,
  data: ExerciseMetricMutationData[],
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const workoutLayerMetrics = Layer(workoutLayer).exerciseMetricAdds({
    workoutExerciseId,
  });
  const baseLayerMetrics = Layer(baseLayer).exerciseMetricAdds({
    workoutExerciseId,
  });

  const normalizedMetrics = ensureSetsMetricInExercise(
    data,
    setsMetric,
    baseLayerMetrics,
  );

  const allExistingMetrics = [...workoutLayerMetrics, ...baseLayerMetrics];

  const newMetricIds = new Set(normalizedMetrics.map((m) => m.metricId));

  const metricsToRemove = allExistingMetrics.filter(
    (metric) => !newMetricIds.has(metric.metricId),
  );

  const builder = new WorkoutLayerBuilder({
    workoutLayer,
  });

  // Remove metrics that are no longer needed
  // Only remove metrics NOT in the new set
  metricsToRemove.forEach((metric) => {
    builder.removeExerciseMetric({
      workoutExerciseMetricId: metric.workoutExerciseMetricId,
    });
  });

  // Add new metrics
  // Metrics that already exist will be filtered out
  const baseLayerSets = Layer(baseLayer).setAdds({ workoutExerciseId });
  builder.addExerciseMetrics({
    workoutExerciseId,
    metrics: normalizedMetrics,
    otherMetrics: baseLayerMetrics,
    otherSets: baseLayerSets,
  });
  return builder.getEdits();
}

export function swapExerciseMetricEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  baseLayer: HasWorkoutLayer | undefined,
  setsMetric: Metric,
  workoutExerciseId: string,
  targetExerciseMetricId: string,
  data: ExerciseMetricMutationData,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const baseLayerMetrics = Layer(baseLayer).exerciseMetricAdds({
    workoutExerciseId,
  });
  const normalizedMetrics = ensureSetsMetricInExercise(
    [data],
    setsMetric,
    baseLayerMetrics,
  );

  const builder = new WorkoutLayerBuilder({
    workoutLayer: workoutLayer?.layer,
  });
  builder.removeExerciseMetric({
    workoutExerciseMetricId: targetExerciseMetricId,
  });
  builder.addExerciseMetrics({ workoutExerciseId, metrics: normalizedMetrics });
  return builder.getEdits();
}

// ============================================================================
// Set operations
// ============================================================================
export function addSetEdits(
  setsMetric: Metric,
  workoutExerciseId: string,
  { sortOrder, workoutSetId, workoutSetGroupId }: SetMutationData,
  metrics: SetMetricMutationData[] = [],
  preserveIds = false,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const normalizedSets = ensureDefaultSetWithSetsMetric(
    [{ sortOrder, workoutSetId, workoutSetGroupId, metrics }],
    setsMetric,
  );

  return new WorkoutLayerBuilder()
    .addSets({ workoutExerciseId, sets: normalizedSets, preserveIds })
    .getEdits();
}

export function editSetEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutSetId: string,
  setMutationData: SetMutationData,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder({ workoutLayer: workoutLayer?.layer })
    .editSet({ workoutSetId, setMutationData })
    .getEdits();
}

export function removeSetEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutSetId: string,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder({ workoutLayer: workoutLayer?.layer })
    .removeSet({ workoutSetId })
    .getEdits();
}

export function removeSetsEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutSetIds: string[],
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const builder = new WorkoutLayerBuilder({
    workoutLayer: workoutLayer?.layer,
  });
  for (const workoutSetId of workoutSetIds) {
    builder.removeSet({ workoutSetId });
  }
  return builder.getEdits();
}

/*
 * Handle removing 1 or more sets from a set group. If the remaining count is
 * greater than 0, we just update the setMetric record. Otherwise, we create a
 * removal
 *
 * @param newSetCount the new number of sets; this needs to be calculated outside of this function
 */
export function removeSetsFromGroup(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutSetId: string,
  setsMetric: Metric,
  newSetCount: number,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const builder = new WorkoutLayerBuilder({ workoutLayer });
  if (newSetCount < 1) {
    return builder.removeSet({ workoutSetId }).getEdits();
  }

  return builder
    .editSetMetric({
      workoutSetId,
      metricId: setsMetric.id,
      data: {
        metricId: setsMetric.id,
        input: String(newSetCount),
      },
    })
    .getEdits();
}

// ============================================================================
// Set metric operations
// ============================================================================
export function editSetMetricEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutSetId: string,
  data: SetMetricMutationData,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder({ workoutLayer: workoutLayer?.layer })
    .editSetMetric({ workoutSetId, metricId: data.metricId, data })
    .getEdits();
}

export function editSetMetricsEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  workoutSetId: string,
  data: SetMetricMutationData[],
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const builder = new WorkoutLayerBuilder({
    workoutLayer: workoutLayer?.layer,
  });
  for (const metric of data) {
    builder.editSetMetric({
      workoutSetId,
      metricId: metric.metricId,
      data: metric,
    });
  }
  return builder.getEdits();
}

// ============================================================================
// MISC operations
// ============================================================================
export function mergeSupersetEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  supersetIdToRemove: string,
  supersetIdToJoin: string,
  updatedSupersetSortOrder: number,
  exerciseSortOrders: SupersetExerciseMutationData[],
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  return new WorkoutLayerBuilder({ workoutLayer: workoutLayer?.layer })
    .mergeSuperset({
      supersetIdToRemove,
      supersetIdToJoin,
      exerciseSortOrders,
      updatedSupersetSortOrder,
    })
    .getEdits();
}

export function swapExerciseEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  modelName: WorkoutLayerModelName,
  setsMetric: Metric,
  workoutSectionId: string,
  previousWorkoutExerciseId: string,
  data: ExerciseMutationData,
  metrics: ExerciseMetricMutationData[],
  sets: SetFullMutationData[] = [],
  superset?: SupersetMutationData,
  alternateGroup?: AlternateGroupMutationData,
  preserveIds = false,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  // If athlete layer, remove the old exercise and add the new one
  // Else if the base layer, update the exercise
  const isAthleteLayer =
    modelName === 'AthleteActualWorkoutLayer' ||
    modelName === 'AthleteDraftWorkoutLayer';

  const normalizedMetrics = ensureSetsMetricInExercise(metrics, setsMetric);
  const normalizedSets = ensureDefaultSetWithSetsMetric(sets, setsMetric);

  return new WorkoutLayerBuilder({
    workoutLayer: workoutLayer?.layer,
  })
    .swapExercise({
      // For athlete layers: removes old exercise and adds new one with new ID
      // For base layers: edits exercise in place and removes incompatible metrics
      replaceExercise: isAthleteLayer,
      workoutSectionId,
      previousWorkoutExerciseId,
      exerciseData: data,
      metrics: normalizedMetrics,
      sets: normalizedSets,
      superset: superset,
      alternateGroup: alternateGroup,
      preserveIds,
    })
    .getEdits();
}

export function swapSupersetExerciseEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  newWorkoutExerciseId: string,
  supersetExercise?: SupersetExerciseFullMutationData,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  if (!supersetExercise) return [];
  return new WorkoutLayerBuilder({ workoutLayer: workoutLayer?.layer })
    .addSupersetExercise({
      workoutExerciseId: newWorkoutExerciseId,
      workoutSupersetId: supersetExercise.workoutSupersetId,
      sortOrder: supersetExercise.sortOrder,
    })
    .removeSupersetExercise({
      workoutSupersetExerciseId: supersetExercise.workoutSupersetExerciseId,
    })
    .getEdits();
}

export function moveEntriesIntoNewSectionEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  setsMetric: Metric,
  entries: EntryFullMutationData[],
  targetSectionId: string,
  targetSortOrder: number,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const normalizedEntries = normalizeEntriesForDraft(entries, setsMetric);

  return new WorkoutLayerBuilder({ workoutLayer: workoutLayer?.layer })
    .moveEntriesIntoNewSection({
      entries: normalizedEntries,
      targetSectionId: targetSectionId,
      targetSortOrder: targetSortOrder,
    })
    .getEdits();
}

// ============================================================================
// Paste operations
// ============================================================================
export function pasteWorkoutEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  setsMetric: Metric,
  baseLayer: HasWorkoutLayer | undefined,
  sections: WorkoutSection[],
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const builder = new WorkoutLayerBuilder({
    workoutLayer: workoutLayer?.layer,
  }).clearWorkout(baseLayer?.layer);

  for (const section of sections) {
    const newSectionId = v4();
    builder.addSection({
      workoutSectionId: newSectionId,
      data: {
        name: section.name,
        sortOrder: section.sortOrder,
        instruction: section.instruction ?? null,
        color: section.color ?? null,
      },
    });

    const normalizedEntries = normalizeEntriesForDraft(
      section.entries,
      setsMetric,
    );

    builder.copyEntriesToSection({
      entries: normalizedEntries,
      targetSectionId: newSectionId,
      targetSortOrder: 0,
    });
  }
  return builder.getEdits();
}

export function pasteSectionEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  setsMetric: Metric,
  sections: SectionGroupedData[],
  targetNewSectionSortOrder: number,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const builder = new WorkoutLayerBuilder({
    workoutLayer: workoutLayer?.layer,
  });

  sections.forEach(({ section }, i) => {
    const newSectionId = v4();
    builder.addSection({
      workoutSectionId: newSectionId,
      data: {
        name: section.name,
        sortOrder: targetNewSectionSortOrder + i,
        instruction: section.instruction ?? null,
        color: section.color ?? null,
      },
    });

    const normalizedEntries = normalizeEntriesForDraft(
      section.entries,
      setsMetric,
    );

    builder.copyEntriesToSection({
      entries: normalizedEntries,
      targetSectionId: newSectionId,
      targetSortOrder: 0,
    });
  });

  return builder.getEdits();
}

export function pasteExerciseEdits(
  workoutLayer: HasWorkoutLayer | undefined,
  setsMetric: Metric,
  entries: EntryFullMutationData[],
  targetNewSectionId: string,
  targetNewExerciseSortOrder: number,
): WorkoutLayerDomain.WorkoutLayerEdit[] {
  const normalizedEntries = normalizeEntriesForDraft(entries, setsMetric);

  return new WorkoutLayerBuilder({
    workoutLayer: workoutLayer?.layer,
  })
    .copyEntriesToSection({
      entries: normalizedEntries,
      targetSectionId: targetNewSectionId,
      targetSortOrder: targetNewExerciseSortOrder,
    })
    .getEdits();
}

// ============================================================================
// Bulk Clear operations
// ============================================================================
export function commandsForBulkClear(
  modelName: WorkoutLayerModelName,
  workoutLayers: HasWorkoutLayer[],
  groupedData: WorkoutGroupedData[],
): Result<WorkoutLayerCommand[], string> {
  const disabledItemPresent = hasDisabledItem(groupedData);
  // TODO: Allow paste if there are the same number of workouts in each, or only one workout in selected
  if (disabledItemPresent) {
    return {
      ok: false,
      error: 'Cannot delete selection',
    };
  }
  const commands: WorkoutLayerCommand[] = [];
  for (const { workoutId, data } of groupedData) {
    const { workoutLayer, identity } = findWorkoutLayer(
      workoutLayers,
      workoutId,
      modelName,
    );

    const builder = new WorkoutLayerBuilder({
      workoutLayer: workoutLayer?.layer,
    });
    let workoutName = workoutLayer?.name ?? null;

    for (const d of data) {
      if (d.type === 'Set') {
        clearSetsAndMetrics(builder, [d.set], d.metrics);
      } else if (d.type === 'Exercise') {
        clearSetsAndMetrics(builder, d.sets, d.exercise.metrics);
      } else if (d.type === 'Section') {
        d.exercises.forEach((e) => {
          clearSetsAndMetrics(builder, e.sets, e.exercise.metrics);
        });
      } else if (d.type === 'WorkoutName') {
        workoutName = null;
      }
    }

    commands.push(
      createWorkoutLayerEdit(
        identity,
        workoutLayer,
        builder.getEdits(),
        workoutName,
      ),
    );
  }

  return buildResponse(commands);
}

function clearSetsAndMetrics(
  builder: WorkoutLayerBuilder,
  sets: WorkoutSet[],
  exerciseMetrics: readonly WorkoutExerciseMetric[],
): void {
  for (const exerciseMetric of exerciseMetrics) {
    for (const set of sets) {
      builder.editSetMetric({
        workoutSetId: set.workoutSetId,
        metricId: exerciseMetric.metricId,
        data: {
          metricId: exerciseMetric.metricId,
          input: '',
        },
      });
    }
  }
}
